import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  tenantService,
  type TenantDetail,
  type UpdateProfileData,
  type UpdateBrandingData,
  type UpdateBackupData,
  type BrandingResponse,
} from '@/services/tenant.service';
import type { AuthTenant } from '@/types/auth';

interface TenantState {
  tenant: TenantDetail | null;
  isLoading: boolean;
  savingProfile: boolean;
  savingBranding: boolean;
  savingBackup: boolean;
  error: string | null;

  fetchTenant: (tenantId: string) => Promise<void>;
  setFromAuthTenant: (authTenant: AuthTenant) => void;
  updateProfile: (tenantId: string, data: UpdateProfileData) => Promise<void>;
  updateBranding: (data: UpdateBrandingData) => Promise<void>;
  updateBackupSettings: (tenantId: string, data: UpdateBackupData) => Promise<void>;
  clearError: () => void;
}

export const useTenantStore = create<TenantState>()(devtools((set, get) => ({
  tenant: null,
  isLoading: false,
  savingProfile: false,
  savingBranding: false,
  savingBackup: false,
  error: null,

  fetchTenant: async (tenantId) => {
    set({ isLoading: true, error: null });
    try {
      const tenant = await tenantService.getTenant(tenantId);
      set({ tenant, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load tenant';
      set({ error: message, isLoading: false });
    }
  },

  // GET /tenants/:id is OWNER-only. Every other tenant role gets the receipt
  // profile /auth/me carries, so its receipts still show the seller's tax
  // number, phone and footer.
  setFromAuthTenant: (authTenant) =>
    set({
      tenant: {
        id: authTenant.id,
        business_name: authTenant.business_name,
        business_type: authTenant.business_type,
        status: authTenant.status,
        subscription_plan: authTenant.subscription_plan,
        default_currency: authTenant.default_currency,
        gstin: authTenant.gstin ?? undefined,
        phone: authTenant.phone ?? undefined,
        receipt_footer: authTenant.receipt_footer ?? undefined,
        show_gstin_on_receipt: authTenant.show_gstin_on_receipt,
      },
    }),

  updateProfile: async (tenantId, data) => {
    set({ savingProfile: true });
    try {
      const tenant = await tenantService.updateProfile(tenantId, data);
      set({ tenant, savingProfile: false });
    } catch (err) {
      set({ savingProfile: false });
      throw err;
    }
  },

  // Backend returns a partial branding object, not a full tenant row.
  // Merge the response fields into the existing tenant to avoid data loss.
  updateBranding: async (data) => {
    set({ savingBranding: true });
    try {
      const result: BrandingResponse = await tenantService.updateBranding(data);
      const existing = get().tenant;
      if (existing) {
        set({
          tenant: {
            ...existing,
            logo_url: result.logo_url ?? undefined,
            primary_color: result.primary_color ?? undefined,
            secondary_color: result.secondary_color ?? undefined,
            receipt_footer: result.receipt_footer ?? undefined,
            invoice_header: result.invoice_header ?? undefined,
            updated_at: result.updated_at ?? existing.updated_at,
          },
          savingBranding: false,
        });
      } else {
        set({ savingBranding: false });
      }
    } catch (err) {
      set({ savingBranding: false });
      throw err;
    }
  },

  updateBackupSettings: async (tenantId, data) => {
    set({ savingBackup: true });
    try {
      const tenant = await tenantService.updateBackupSettings(tenantId, data);
      set({ tenant, savingBackup: false });
    } catch (err) {
      set({ savingBackup: false });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}), { name: 'tenant-store' }));
